import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ApiError, AuthService, ButtonLoader, ConfigService, MessageService, notCloseMessageOptions } from '../../../../../../core';
import { CampanhaSelectComponent } from '../../../../../campanhas/campanhas/components/select';
import { CampanhaSelect, mapToCampanha } from '../../../../../campanhas/campanhas/components/select/campanha-select';
import { CanalVendaSelectComponent } from '../../../../../common/canais-venda/components/select';
import { CanalMidiaSocialSelectComponent } from '../../../../../common/canal-midia-social/components/select';
import { OrigemSelectComponent } from '../../../../../common/origens/components/select';
import { ConfigEmpresa } from '../../../../../configuracoes/app/empresa';
import { ClienteValidacoesHttpService } from '../../../../../pessoa/cliente/validacoes';
import { UsuarioSelectComponent } from '../../../../../usuario/select';
import { Usuario } from '../../../../../usuario/usuario';
import { LeadIntegracaoEmpresasImportHttpService } from '../../../integracoes/empresas/services';
import { Lead } from '../../../lead';
import { LeadValidacoesHttpService } from '../../../validacoes';
import { LeadPreInsert } from './lead-pre-insert';

interface Config {
    podeCadastrarLeadExterior: boolean;
}

@Component({
    selector: 'sgt-lead-pre-insert-form',
    templateUrl: 'lead-pre-insert-form.component.html',
    styleUrls: ['./lead-pre-insert-form.component.less']
})
export class LeadPreInsertFormComponent implements OnInit {
    config: Config;
    configEmpresa: ConfigEmpresa;
    entity: LeadPreInsert;

    // Permissões do usuários
    usuarioPodeSalvarCnpjCpfInvalido: boolean; // Se o usuário pode salvar o CNPJ/CPF inválido
    ignorarCnpjCpfInvalido = false; // Se deve ignorar o erro de CNPJ/CPF inválido

    /** Se deve exibir o campo da origem */
    @Input() isShowOrigem = true;
    /** Se deve exibir o usuário responsável */
    @Input() isShowUsuarioResponsavel = true;
    /** Se deve exibir a temperatura */
    @Input() isShowTemperatura = true;
    /** Nome do lead */
    @Input() nome: string = null;

    @ViewChild('form') form: NgForm;
    @ViewChild('origemSelect') origemSelect: OrigemSelectComponent;
    @ViewChild('campanhaOrigemSelect') campanhaOrigemSelect: CampanhaSelectComponent;
    @ViewChild('canalVendaOrigemSelect') canalVendaOrigemSelect: CanalVendaSelectComponent;
    @ViewChild('canalMidiaSocialSelect') canalMidiaSocialSelect: CanalMidiaSocialSelectComponent;
    @ViewChild('usuarioSelect') usuarioSelect: UsuarioSelectComponent;
    @ViewChild('cnpjCpfInput') cnpjCpfInput: ElementRef;
    @ViewChild('nomeInput') nomeInput: ElementRef;
    @ViewChild('btnConfirm') btnConfirm: ElementRef;

    @Output() confirm = new EventEmitter<LeadPreInsert>();

    constructor(private authService: AuthService,
                private configService: ConfigService,
                private messageService: MessageService,
                private leadValidacoesHttpService: LeadValidacoesHttpService,
                private leadIntegracaoEmpresasImportHttpService: LeadIntegracaoEmpresasImportHttpService,
                private clienteValidacoesHttpService: ClienteValidacoesHttpService) { }

    ngOnInit(): void {
        this.configEmpresa = this.configService.getEmpresa();
        this.entity = new LeadPreInsert();
        this.entity.nome = this.nome;
        // Permissões do usuários
        this.usuarioPodeSalvarCnpjCpfInvalido = this.authService.podeSalvarCnpjCpfInvalido();

        // Setando o usuário logado como o lead sendo inserido
        this.setUsuarioLogadoComoResponsavel();

        this.setConfig();
        setTimeout(() => this.setFocusOrigem(), 300);
    }

    private setConfig() {
        this.config = {
            podeCadastrarLeadExterior: this.configEmpresa?.exportadora && this.authService.podeCadastrarClienteExterior()
        }
    }

    /** Seta o usuário logado como o responsável pelo lead */
    private setUsuarioLogadoComoResponsavel() {
        const usuarioResponsavel = this.authService.getUsuario();
        if (usuarioResponsavel) {
            this.entity.idUsuarioResponsavel = usuarioResponsavel.id;
            this.entity.usuarioResponsavel = usuarioResponsavel;
        }
    }

    /** Ao alterar o usuário responsável pelo lead */
    onChangeUsuarioResponsavel(usuarioResponsavel: Usuario) {
        if (usuarioResponsavel) {
            this.entity.idUsuarioResponsavel = usuarioResponsavel.id;
        } else {
            this.entity.idUsuarioResponsavel = null;
        }
    }

    /** Ao alterar a origem */
    onChangeOrigem(origem: number) {
        this.entity.origem = origem;

        setTimeout(() => {
             if (this.entity.isOrigemPossuiCanalVenda()) {
                this.setFocusCanalVendaOrigem();
            } else if (this.entity.isOrigemCampanha()) {
                this.setFocusCampanhaOrigem();
            } else if (this.entity.isOrigemMidiaSocial()) {
                this.setFocusCanalMidiaSocial();
            } else {
                if (this.entity.isTipoPessoaFisicaOuJuridica()) {
                    this.setFocusCnpjCPF();
                }
            }
        }, 100);
    }

    /** Ao alterar o tipo da pessoa */
    onChangeTipoPessoa(tipoPessoa: number) {
        this.entity.tipoPessoa = tipoPessoa;
        if (this.entity.isTipoPessoaFisicaOuJuridica()) {
            setTimeout(() => this.setFocusCnpjCPF(), 0);
        }
    }

    /** Seta foco no select da origem */
    private setFocusOrigem() {
        this.origemSelect?.setFocus();
    }

    /** Seta foco no select da campanha de origem */
    private setFocusCampanhaOrigem() {
        this.campanhaOrigemSelect?.setFocus();
    }

    /** Seta foco no select do canal de venda de origem */
    private setFocusCanalVendaOrigem() {
        this.canalVendaOrigemSelect?.setFocus();
    }

    /** Seta foco no select do canal da mídia social */
    private setFocusCanalMidiaSocial() {
        this.canalMidiaSocialSelect?.setFocus();
    }

    /** Seta foco no select do usuário responsável */
    private setFocusUsuarioResponsavel() {
        this.usuarioSelect?.setFocus();
    }

    /** Seta foco no input do CNPJ/CPF */
    private setFocusCnpjCPF() {
        jQuery(this.cnpjCpfInput?.nativeElement).trigger('focus');
    }

    /** Seta foco no input do nome */
    private setFocusNome() {
        jQuery(this.nomeInput?.nativeElement).trigger('focus');
    }

    /** Ao alterar a campanha de origem do lead */
    onChangeCampanhaOrigem(campanhaOrigem: CampanhaSelect) {
        this.entity.campanhaOrigem = mapToCampanha(campanhaOrigem);
    }

    /** Verifica se o CNPJ/CPF é obrigatório */
    isCnpjCpfRequired(): boolean {
        if (!this.entity) {
            return false;
        }

        // Se a origem for campanha e a campanha permitir cadastrar leads sem CNPJ/CPF, então não é obrigatório
        if (this.entity.isOrigemCampanha() && this.entity.campanhaOrigem && this.entity.campanhaOrigem.permitirCadastrarLeadSemCnpjCpf) {
            return false;
        }

        // Somente quando for uma pessoa do tipo Física ou Juridica que não seja do exterior
        return this.entity?.isTipoPessoaJuridica() && !this.entity.exterior;
    }

    /** Verifica se deve validar o CNPJ/CPF */
    mustValidateCnpjCpf(): boolean {
        if (!this.entity) {
            return true;
        }

        // Se o usuário poder salvar com o CNPJ/CPF e estiver selecionado para ignorar o erro de validação, então não deve validar
        if (this.usuarioPodeSalvarCnpjCpfInvalido && this.ignorarCnpjCpfInvalido) {
            return false;
        }

        // Se for do exterior, então não deve validar
        if (this.entity.exterior) {
            return false;
        }

        return true;
    }

    /** Realiza a validação de cadastro já existe do lead e pessoa através do CNPJ/CPF informado */
    private validadeLeadJaCadastro(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (!this.entity) {
                resolve(false);
                return;
            }
            // Se for um lead do exterior, então ignoramos a validação pois o mesmo não possui um CNPJ
            if (this.entity.exterior) {
                resolve(true);
                return;
            }
            // Se for uma central de compras não precisa validar
            if (this.entity.isTipoPessoaCentralCompras()) {
                resolve(true);
                return;
            }

            this.leadValidacoesHttpService.getLeadJaCadastrado(null, this.entity.cnpjCpf, false)
                .subscribe(
                    validacao => {
                        // Verificando se o CNPJ Matriz (parte até a "/") já esta cadastrado
                        if (validacao.cnpjMatrizJaCadastrado) {
                            this.messageService.warning('Não é possível cadastrar o lead por que já existe um outro lead cadastrado com o CNPJ da Matriz igual ao informado. Por favor verifique o cadastro de leads ou entre em contato com o comercial para mais informações', 'Atenção', notCloseMessageOptions);
                            resolve(false);
                            return;
                        }

                        // Verificando se o lead já está cadastrado para o representante ou para a empresa
                        if (validacao.jaCadastrado) {
                            this.messageService.warning('Já existe um lead cadastrado com este CNPJ/CPF informado. Por favor verifique o cadastro de leads ou entre em contato com o comercial para mais informações', 'Atenção', notCloseMessageOptions);
                            resolve(false);
                            return;
                        }

                        resolve(true);
                    },
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Ocorreu um erro ao verificar se o já existe um lead com o CNPJ/CPF cadastrado', error);
                        resolve(false);
                    }
                );
        });
    }

    /** Valida se já existe um lead cadastrado com o mesmo CNPJ do lead informado */
    private validadeClienteJaCadastro(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (!this.entity) {
                resolve(false);
                return;
            }
            // Se for um lead do exterior, então ignoramos a validação pois o mesmo não possui um CNPJ
            if (this.entity.exterior) {
                resolve(true);
                return;
            }
            // Se for uma central de compras não precisa validar
            if (this.entity.isTipoPessoaCentralCompras()) {
                resolve(true);
                return;
            }

            this.clienteValidacoesHttpService.getClienteJaCadastrado(null, this.entity.cnpjCpf, null, false)
            .subscribe(
                validacao => {
                    // Verificando se o CNPJ Matriz (parte até a "/") já esta cadastrado
                    if (validacao.cnpjMatrizJaCadastrado) {
                        this.messageService.warning('Não é possível cadastrar o lead por que já existe um prospect ou cliente cadastrado com o CNPJ da Matriz igual ao informado. Por favor verifique o cadastro de prospects e clientes ou entre em contato com o comercial para mais informações', 'Atenção', notCloseMessageOptions);
                        resolve(false);
                        return;
                    }

                    // Verificando se o cliente já está cadastrado para o representante ou para a empresa
                    if (validacao.jaCadastrado) {
                        this.messageService.warning('Já existe um prospect ou cliente cadastrado com este CNPJ/CPF informado. Por favor verifique o cadastro de prospects e clientes ou entre em contato com o comercial para mais informações', 'Atenção', notCloseMessageOptions);
                        resolve(false);
                        return;
                    }

                    // Sem permissão para cadastrar clientes secundários
                    if (validacao.clienteSecundarioNaoPermitido) {
                        this.messageService.warning('O CNPJ/CPF informado já está cadastrado para outro prospect ou cliente e não é permitido cadastrar clientes secundários. Por favor verifique o cadastro de prospects e clientes ou entre em contato com o comercial para mais informações', 'Atenção', notCloseMessageOptions);
                        resolve(false);
                        return;
                    }

                    // Verificando se o cliente já está cadastrado como cliente primario para outro representante ou para a empresa
                    if (validacao.clientePrimarioJaCadastrado) {
                        this.messageService.warning('Já existe um prospect ou cliente cadastrado com este CNPJ/CPF informado mas que não pertence a sua carteira de clientes. Por favor verifique o cadastro de prospects e clientes ou entre em contato com o comercial para mais informações', 'Atenção', notCloseMessageOptions);
                        resolve(false);
                        return;
                    }

                    // Verificando se a pessoa já está cadastrada
                    if (validacao.pessoaJaCadastrada) {
                        this.messageService.warning('Já existe um prospect ou cliente cadastrado com este CNPJ/CPF informado. Por favor verifique o cadastro de prospects e clientes ou entre em contato com o comercial para mais informações', 'Atenção', notCloseMessageOptions);
                        resolve(false);
                        return;
                    }

                    resolve(true);
                },
                (error: ApiError) => {
                    this.messageService.error(error.message, 'Ocorreu um erro ao verificar se o já existe um prospect ou cliente com o CNPJ/CPF cadastrado', error);
                    resolve(false);
                }
            );
        });
    }

    /** Verificando se os dados de pré-insert está válidos */
    isValid(): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            // Origem não selecionada
            if (!this.origemSelect.isValid) {
                this.messageService.warning('Selecione a origem deste lead e tente novamente', 'Origem do lead não selecionada', notCloseMessageOptions);
                this.setFocusOrigem();
                resolve(false);
                return;
            }

            // Campanha
            if (this.entity.isOrigemCampanha()) {
                if (!this.entity.idCampanhaOrigem) {
                    this.messageService.warning('Selecione a campanha de origem deste lead e tente novamente', 'Campanha de origem do lead não selecionada', notCloseMessageOptions);
                    this.setFocusCampanhaOrigem();
                    resolve(false);
                    return;
                }
            }

            // Canal de venda de origem
            if (this.entity.isOrigemPossuiCanalVenda() && !this.canalVendaOrigemSelect?.isValid) {
                this.messageService.warning('Selecione o canal de venda de origem deste lead e tente novamente', 'Canal de venda de origem não selecionado', notCloseMessageOptions);
                this.setFocusCanalVendaOrigem();
                resolve(false);
                return;
            }

            // Canal da mídia social
            if (this.entity.isOrigemMidiaSocial()) {
                if (!this.canalMidiaSocialSelect.isValid) {
                    this.messageService.warning('Selecione o canal da mídia social que gerou este lead e tente novamente', 'Canal da mídia social não selecionado', notCloseMessageOptions);
                    this.setFocusCanalMidiaSocial();
                    resolve(false);
                    return;
                }
            }

            // Usuário responsável
            if (this.isShowUsuarioResponsavel && !this.entity.idUsuarioResponsavel) {
                this.messageService.warning('Selecione o responsável por este lead e tente novamente', 'Responsável não selecionado', notCloseMessageOptions);
                this.setFocusUsuarioResponsavel();
                resolve(false);
                return;
            }

            // Nome
            if (!this.entity.nome || this.entity.nome.trim() == '') {
                this.messageService.warning('Informe o nome do lead e tente novamente', 'Nome do lead não informado', notCloseMessageOptions);
                this.setFocusNome();
                resolve(false);
                return;
            }

            // Pessoa Física
            if (this.entity.isTipoPessoaFisica()) {
                if (this.isCnpjCpfRequired() && !this.entity.cnpjCpf) {
                    this.messageService.warning('Informe o CPF do lead e tente novamente', 'CPF não informado', notCloseMessageOptions);
                    this.setFocusCnpjCPF();
                    resolve(false);
                    return;
                }
            }

            // Pessoa Jurídica
            if (this.entity.isTipoPessoaJuridica()) {
                if (this.isCnpjCpfRequired() && !this.entity.cnpjCpf) {
                    this.messageService.warning('Informe o CNPJ do lead e tente novamente', 'CNPJ não informado', notCloseMessageOptions);
                    this.setFocusCnpjCPF();
                    resolve(false);
                    return;
                }
            }

            // Form inválido
            if (this.form.invalid) {
                this.messageService.warning('Verifique os dados informados e tente novamente', 'Dados inválidos', notCloseMessageOptions);
                resolve(false);
                return;
            }

            // Validando cadastro do lead
            const isLeadValid = await this.validadeLeadJaCadastro();
            if (!isLeadValid) {
                resolve(false);
                return;
            }

            // Validando cadastro do cliente
            const isClienteValid = await this.validadeClienteJaCadastro();
            if (!isClienteValid) {
                resolve(false);
                return;
            }

            resolve(true);
        });
    }

    /** Realiza a consulta do CNPJ de uma empresas */
    consultarCnpj(): Promise<Lead> {
        return new Promise<Lead>((resolve, reject) => {
            if (this.isCnpjCpfRequired() && (!this.entity.cnpjCpf || this.entity.cnpjCpf === '')) {
                this.messageService.warning('Informe um CNPJ válido para poder consultá-lo', 'Atenção');
                this.setFocusCnpjCPF();
                reject();
                return;
            } else if (!this.entity.cnpjCpf) {
                resolve(null);
                return;
            }

            const btn = new ButtonLoader(this.btnConfirm.nativeElement);
            btn.loading('Consultando CNPJ')
            this.leadIntegracaoEmpresasImportHttpService.consultaCNPJ(this.entity.cnpjCpf)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => {
                        resolve(response);
                        btn.reset();
                    },
                    (error: ApiError) => {
                        btn.reset();

                        // Nenhum CNPJ encontrado na base de dados (Pode ser por cliente baixado mas por cliente novo que ainda não foi inserido na base de CNPJ da Receita Federal)
                        if (error.status == 404) {
                            this.messageService.warning(error.message, error.cause, notCloseMessageOptions);
                            resolve(null);
                            return;
                        }

                        this.messageService.error(error.message, error.cause, error);
                        reject();
                    }
                );
        });
    }

    /** Ao confirmar o pré-insert */
    async confirmClick() {
        try {
            // Validando cadastro
            const isValid = await this.isValid();
            if (!isValid) {
                return;
            }

            if (this.entity.isTipoPessoaJuridica() && !this.entity.exterior) { // Pessoa jurídica consulta o CNPJ da empresa
                this.entity.leadPessoaJuridica = await this.consultarCnpj();
            } else {
                this.entity.leadPessoaJuridica = null;
            }

            this.confirm.emit(this.entity);
        } catch (error) {
            //
        }
    }
}
